import { signal, effect } from '@preact/signals';
import { secureStorage } from '../storage/secureStorage.js';

/**
 * Currently-selected bible (translation) + language for the reader.
 *
 * `bibleId` is the BB `abbr` (LLLVVV, e.g. ENGESV, SPABDA, KMHKMV).
 * `languageIso` is ISO 639-3 (e.g. eng, spa, kmh). `displayName` is the
 * BB `vname` or `name` for chrome.
 *
 * Persisted to localStorage so the user's pick survives across sessions.
 */

export type BibleSelection = {
  bibleId: string;
  languageIso: string;
  displayName: string;
  /** Derived from BB fileset size codes. Undefined = unknown (treat as true). */
  hasOT?: boolean;
  hasNT?: boolean;
};

const DEFAULT: BibleSelection = {
  bibleId: 'ENGESV',
  languageIso: 'eng',
  displayName: 'English Standard Version',
};

const STORAGE_KEY = '_b1';

function load(): BibleSelection {
  if (typeof localStorage === 'undefined') return DEFAULT;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw) as Partial<BibleSelection>;
    if (
      typeof parsed.bibleId === 'string' &&
      typeof parsed.languageIso === 'string' &&
      typeof parsed.displayName === 'string'
    ) {
      return parsed as BibleSelection;
    }
    return DEFAULT;
  } catch {
    return DEFAULT;
  }
}

export const currentBible = signal<BibleSelection>(load());

if (typeof window !== 'undefined') {
  effect(() => {
    void secureStorage.setItem(STORAGE_KEY, JSON.stringify(currentBible.value));
  });
}

export function setBible(selection: BibleSelection): void {
  if (
    currentBible.peek().bibleId === selection.bibleId &&
    currentBible.peek().languageIso === selection.languageIso
  ) {
    return;
  }
  currentBible.value = selection;
}

/** Secondary bible for the side-by-side compare view. `null` = compare off. */
const COMPARE_KEY = '_cb1';

function loadCompare(): BibleSelection | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COMPARE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BibleSelection>;
    if (
      typeof parsed.bibleId === 'string' &&
      typeof parsed.languageIso === 'string' &&
      typeof parsed.displayName === 'string'
    ) {
      return parsed as BibleSelection;
    }
    return null;
  } catch {
    return null;
  }
}

export const compareBible = signal<BibleSelection | null>(loadCompare());

if (typeof window !== 'undefined') {
  effect(() => {
    if (compareBible.value) {
      void secureStorage.setItem(COMPARE_KEY, JSON.stringify(compareBible.value));
    } else {
      secureStorage.removeItem(COMPARE_KEY);
    }
  });
}

export function setCompareBible(selection: BibleSelection | null): void {
  compareBible.value = selection;
}
